import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";

export const getElementCoords = (e: React.MouseEvent<HTMLElement>) => {
	const { currentTarget: target, clientX, clientY } = e;

	if (!target) {
		return {
			x: null,
			y: null,
		};
	}

	const x = clientX;
	const y = clientY;

	return { x, y };
};

export const getUniformValue = <T>(values: T, defaultValue?: T) => {
	const defaultReturnValue = defaultValue ?? "";

	if (!Array.isArray(values) || values.length === 0) {
		return defaultReturnValue;
	}

	const firstValue = values[0];
	const allSame = values.every((value) => value === firstValue);

	return allSame ? firstValue : defaultReturnValue;
};

export const getFirstSeatNumber = (data: Array<EventInventoryRecord>) => {
	let lowestSeat = Infinity;

	data.forEach((x) => {
		if (typeof x.Seats === "string") {
			const seatNumbers = x.Seats.split(",").map((s) => parseInt(s.trim(), 10));
			const minSeat = Math.min(...seatNumbers);

			if (!isNaN(minSeat) && minSeat < lowestSeat) {
				lowestSeat = minSeat;
			}
		}
	});

	return lowestSeat === Infinity ? "" : String(lowestSeat);
};

export const displayOrDash = (value: string) => {
	return value !== "" ? value : "-";
};

export function mapListingQtyToMinQty(qty: number) {
	if (!Number.isFinite(qty)) return "";
	if (qty === 1) return "1";
	if (qty === 2) return "2";
	if (qty === 3) return "3";
	if (qty === 4) return "4";
	if (qty === 5) return "3";
	if (qty >= 6) return "4";
	return "";
}

export const handlePriceVueDownload = (): void => {
	if (typeof window === "undefined") return;

	window.open(
		"https://sfapprelease.blob.core.windows.net/pricevue/sf-chrome-extension-tm-price-filter.zip",
		"_blank",
		"noopener,noreferrer",
	);
};

export const formatLastUpdate = (lastPriceUpdate?: string): string => {
	const match = lastPriceUpdate?.match(/^(\d{2}\/\d{2}\/\d{4})\s+(\d{1,2}:\d{2}\s+[AP]M)\s+(.+)$/);

	if (!match) return "";

	const [, datePart, timePart, restOfString] = match;

	// Source timestamp is stored in server time
	const serverDate = new Date(`${datePart} ${timePart} GMT-0500`);

	const month = String(serverDate.getMonth() + 1).padStart(2, "0");
	const day = String(serverDate.getDate()).padStart(2, "0");
	const year = serverDate.getFullYear();

	let hours = serverDate.getHours();
	const minutes = String(serverDate.getMinutes()).padStart(2, "0");
	const ampm = hours >= 12 ? "PM" : "AM";
	hours = hours % 12 || 12;

	// User/browser timezone offset in minutes
	const offset = -serverDate.getTimezoneOffset();

	const timezoneMap: Record<string, string> = {
		// North America
		"-600": "HST", // Hawaii
		"-540": "AKST", // Alaska Standard
		"-480": "PST", // Pacific Standard
		"-420": "MST", // Mountain Standard / Arizona
		"-360": "CST", // Central Standard
		"-300": "EST", // Eastern Standard
		"-240": "AST", // Atlantic Standard
		"-210": "NST", // Newfoundland Standard

		// Europe / Africa
		"0": "GMT", // UK winter / UTC
		"60": "CET", // Central Europe standard
		"120": "EET", // Eastern Europe standard / South Africa
		"180": "MSK", // Moscow
		"240": "GST", // Gulf Standard
		"60_dst": "CEST",
		"0_dst": "BST", // UK summer
		"120_dst": "EEST",

		// Asia
		"300": "PKT", // Pakistan
		"330": "IST", // India
		"345": "NPT", // Nepal
		"360": "BST_BD", // Bangladesh
		"420": "ICT", // Thailand / Vietnam
		"480": "CST", // China Standard
		"540": "JST", // Japan / Korea
		"570": "ACST", // Australia Central Standard
		"600": "AEST", // Australia Eastern Standard
		"660": "AEDT", // Australia Eastern Daylight
		"720": "NZST", // New Zealand Standard
		"780": "NZDT", // New Zealand Daylight

		// DST variants for US / Canada / AU
		"-480_dst": "PDT",
		"-420_dst": "MDT",
		"-360_dst": "CDT",
		"-300_dst": "EDT",
		"-240_dst": "ADT",
		"-210_dst": "NDT",
		"570_dst": "ACDT",
		"600_dst": "AEST",
	};

	const browserTimeZoneName =
		new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
			.formatToParts(serverDate)
			.find((part) => part.type === "timeZoneName")?.value || "";

	const isDST = /DT|ST|BST|CEST|EEST|AEDT|NZDT|ACDT/i.test(browserTimeZoneName)
		? /DT|BST|CEST|EEST|AEDT|NZDT|ACDT/i.test(browserTimeZoneName)
		: false;

	const tzKey = isDST ? `${offset}_dst` : offset.toString();
	const tzAbbr = timezoneMap[tzKey] || timezoneMap[offset.toString()];

	// Fallback: show original DB value exactly as-is
	if (!tzAbbr) return lastPriceUpdate ?? "";

	return `${month}/${day}/${year} ${hours}:${minutes} ${ampm} ${tzAbbr} ${restOfString}`;
};

export const lowercaseCompare = (x: string, y: string) => {
	if (!x || !y) return true;
	return x.toLocaleLowerCase().includes(y.toLocaleLowerCase());
};
